import './App.css';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { RotateCcw } from 'lucide-react';
import Navigation from './components/Navigation';
import LoadingScreen from './components/LoadingScreen';
import ExhibitSelector from './components/ExhibitSelector';
import WelcomeModal from './components/WelcomeModal';
import PageHeader from './components/PageHeader';
import MyExhibits from './pages/MyExhibits';
import { ExhibitProvider } from './context/ExhibitContext';
import { getImageUrl, delay } from './scripts/utils';

function Home({ textSize, onTextSizeChange, onShowWelcomeModal }) {
  const [allArtworks, setAllArtworks] = useState([]);
  const [displayedArtworks, setDisplayedArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingNewArtwork, setLoadingNewArtwork] = useState(false);
  const [showDailyLimitMessage, setShowDailyLimitMessage] = useState(false);
  const [selectedArtwork, setSelectedArtwork] = useState(null);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  // Simple function to fetch 1 artwork from The Met API
  // localStorage functions for persistence
  const saveArtworkToStorage = (artwork) => {
    localStorage.setItem('currentArtwork', JSON.stringify(artwork));
  };

  const loadArtworkFromStorage = useCallback(() => {
    const stored = localStorage.getItem('currentArtwork');
    return stored ? JSON.parse(stored) : null;
  }, []);
  
  // Function to clear stored artwork (useful for testing)
  const clearStoredArtwork = useCallback(() => {
    localStorage.removeItem('currentArtwork');
    console.log('Stored artwork cleared');
  }, []);
  
  // Make function available globally for debugging
  useEffect(() => {
    window.clearStoredArtwork = clearStoredArtwork;
  }, [clearStoredArtwork]);

  const fetchArtworks = useCallback(async () => {
    let foundArtworks = [];
    
    try {
      console.log('Fetching 10 artworks from The Met API...');
      
      // Get all objects with images from The Met
      const searchUrl = `https://collectionapi.metmuseum.org/public/collection/v1/search?hasImages=true&q=*`;
      const searchResp = await fetch(searchUrl);
      
      if (!searchResp.ok) {
        throw new Error('Search failed');
      }
      
      const searchData = await searchResp.json();
      console.log(`Found ${searchData.total} objects with images`);
      
      if (!searchData.objectIDs || searchData.objectIDs.length === 0) {
        throw new Error('No objects found');
      }
      
      // Keep trying random objects until we find 10 valid public domain artworks with images
      const targetCount = 10;
      const maxAttempts = 50; // Increased attempts for finding 10 artworks
      
      for (let attempt = 0; attempt < maxAttempts && foundArtworks.length < targetCount; attempt++) {
        // Add a small delay between requests to avoid rate limiting
        if (attempt > 0) {
          await delay(100); // 100ms delay between requests
        }
        
        // Pick a completely random object ID
        const randomIndex = Math.floor(Math.random() * searchData.objectIDs.length);
        const randomID = searchData.objectIDs[randomIndex];
        
        console.log(`Attempt ${attempt + 1}: Trying object ID ${randomID}`);
        
        try {
          const objectResp = await fetch(`https://collectionapi.metmuseum.org/public/collection/v1/objects/${randomID}`);
          if (!objectResp.ok) {
            console.log(`Object ${randomID}: HTTP ${objectResp.status}`);
            continue;
          }
          
          const obj = await objectResp.json();
          console.log(`Object ${randomID}:`, {
            title: obj.title,
            isPublicDomain: obj.isPublicDomain,
            hasImage: !!(obj.primaryImageSmall || obj.primaryImage)
          });
          
          // Only use if it's public domain and has an image
          if (obj.isPublicDomain && (obj.primaryImageSmall || obj.primaryImage)) {
            const artwork = {
              id: obj.objectID,
              title: obj.title || 'Untitled',
              artist: obj.artistDisplayName || 'Unknown Artist',
              year: obj.objectDate || 'Unknown Date',
              period: obj.period || obj.classification || 'Unknown Period',
              image_id: obj.primaryImageSmall || obj.primaryImage,
              department: obj.department || '',
              link: obj.objectURL || '',
              tags: [obj.classification, obj.department].filter(Boolean).slice(0, 2)
            };
            
            console.log(`Successfully found valid artwork ${foundArtworks.length + 1}/8:`, artwork.title);
            
            // Add this artwork to our collection
            foundArtworks.push(artwork);
          }
          
        } catch (error) {
          console.log(`Error fetching object ${randomID}:`, error.message);
        }
      }
      
      // Check if we found enough artworks
      if (foundArtworks.length > 0) {
        console.log(`Successfully loaded ${foundArtworks.length} artworks`);
        setAllArtworks(foundArtworks);
        saveArtworkToStorage(foundArtworks);
        return; // Success! Exit the function
      }
      
      // If we get here, we couldn't find enough artworks after all attempts
      throw new Error(`Could not find enough artworks after multiple attempts. Found ${foundArtworks.length}/10`);
      
    } catch (err) {
      console.error('Failed to fetch artworks from Met API:', err.message);
      console.log(`API limit reached after ${foundArtworks.length > 0 ? `finding ${foundArtworks.length} artworks` : 'API failure'}`);
      
      // Show daily limit message instead of fallback artworks
      setShowDailyLimitMessage(true);
      setAllArtworks([]);
    }
  }, []);

  // Update displayed artworks when data loads
  useEffect(() => {
    setDisplayedArtworks(allArtworks);
  }, [allArtworks]);

  // Initial load
  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      
      // Check if we have stored artwork first
      const storedArtwork = loadArtworkFromStorage();
      if (storedArtwork && storedArtwork.length > 0) {
        console.log(`Loading ${storedArtwork.length} artworks from localStorage`);
        setAllArtworks(storedArtwork);
        setShowDailyLimitMessage(false); // Reset message when loading stored artwork
        setLoading(false);
        return;
      }
      
      // Record start time for minimum loading duration
      const startTime = Date.now();
      const minimumLoadingTime = 3000; // Reduced to 3 seconds for faster UX
      
      try {
        // Fetch the artworks only if nothing stored
        await fetchArtworks();
        
        // Calculate how much time has passed
        const elapsedTime = Date.now() - startTime;
        const remainingTime = minimumLoadingTime - elapsedTime;
        
        // If we need to wait longer to reach minimum time, do so
        if (remainingTime > 0) {
          await delay(remainingTime);
        }
        
      } catch (error) {
        console.error('Error loading artworks:', error);
        // Even on error, wait the minimum time for consistent UX
        const elapsedTime = Date.now() - startTime;
        const remainingTime = minimumLoadingTime - elapsedTime;
        if (remainingTime > 0) {
          await delay(remainingTime);
        }
      } finally {
        setLoading(false);
      }
    };
    
    loadInitialData();
  }, [loadArtworkFromStorage, fetchArtworks]);

  // Function to fetch new artwork when user clicks the button
  const fetchNewArtwork = async () => {
    setLoadingNewArtwork(true);
    setShowDailyLimitMessage(false); // Reset the message when trying again
    try {
      await fetchArtworks();
    } catch (error) {
      console.error('Error fetching new artwork:', error);
    } finally {
      setLoadingNewArtwork(false);
    }
  };

  // Function to handle closing the welcome modal
  const handleCloseWelcomeModal = () => {
    setShowWelcomeModal(false);
  };

  return (
    <div className="App">
      <a href="#main-content" className="skip-nav">Skip to main content</a>
      <Navigation 
        currentPage="home" 
        textSize={textSize} 
        onTextSizeChange={onTextSizeChange}
      />
      <PageHeader 
        title="Artwork Gallery"
        description="Welcome to the Artwork Gallery! Discover masterpieces from The Metropolitan Museum of Art's collection. Each time you visit, you'll see a curated selection of beautiful artworks. Click 'New Artwork!' to refresh and discover more treasures. Hover over any artwork to learn more about it, or click to view it in detail."
        showButton={true}
        buttonText={loadingNewArtwork ? 'Loading...' : 'New Artwork!'}
        buttonIcon={<RotateCcw size={20} />}
        onButtonClick={fetchNewArtwork}
        buttonDisabled={loadingNewArtwork}
        buttonClassName="load-more-btn header-btn"
        textSize={textSize}
      />
      {loading ? (
        <LoadingScreen />
      ) : (
        <>
          {/* Status announcements for screen readers */}
          <div aria-live="polite" aria-atomic="true" className="visually-hidden">
            {showDailyLimitMessage && "Daily artwork limit reached. Please try again tomorrow."}
            {loadingNewArtwork && "Loading new artworks..."}
          </div>
          
          {showDailyLimitMessage ? (
            <div className="daily-limit-message" style={{ 
              textAlign: 'center', 
              padding: '60px 40px',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              <h2 style={{ 
                fontSize: '2rem', 
                marginBottom: '20px', 
                color: '#333',
                fontFamily: "'DM Sans', sans-serif"
              }}>
                🎨 You've explored enough art for today!
              </h2>
              <p style={{ 
                fontSize: '1.1rem', 
                lineHeight: '1.6', 
                color: '#222',
                marginBottom: '20px'
              }}>
                You've reached your daily limit for discovering new artworks. This helps us keep the service running smoothly for everyone.
              </p>
              <p style={{ 
                fontSize: '1rem', 
                color: '#333'
              }}>
                Come back tomorrow to discover more amazing pieces from The Met's collection, or browse your saved exhibits in the meantime!
              </p>
            </div>
          ) : (
            <main className="gallery" role="main" aria-label="Artwork gallery" id="main-content">
        {displayedArtworks.map((artwork) => (
          <article key={artwork.id} className="artwork-card">
            <div className="artwork-image">
              {artwork.image_id ? (
                <img 
                  src={getImageUrl(artwork.image_id)} 
                  alt={`${artwork.title} by ${artwork.artist}, ${artwork.year}`}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    // Optional: could render a fallback block here
                  }}
                />
              ) : (
                <div className="no-image" role="img" aria-label="No image available">
                  No Image Available
                </div>
              )}
              <div className="bookmark-container">
                <button 
                  className="bookmark-btn"
                  onClick={() => setSelectedArtwork(selectedArtwork?.id === artwork.id ? null : artwork)}
                  aria-label={`Add ${artwork.title} to my exhibits`}
                  title="Add to My Exhibits"
                >
                  +
                </button>
              </div>
            </div>
            <div className="artwork-info">
              {artwork.link ? (
                <a
                  href={artwork.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="artwork-title-link"
                  aria-label={`View ${artwork.title} on The Met website (opens in new tab)`}
                >
                  <h3 className="artwork-title">{artwork.title} <span className="external-link-icon">↗</span></h3>
                </a>
              ) : (
                <h3 className="artwork-title">{artwork.title}</h3>
              )}
              <p className="artwork-meta">{artwork.artist} • {artwork.year}</p>
              <div className="artwork-tags" aria-label="Artwork categories">
                {artwork.tags.map((tag, index) => (
                  <span key={index} className={`tag ${tag.toLowerCase().replace(/\s+/g, '-')}`}>
                    {tag.toUpperCase()}
                  </span>
                ))}
              </div>
            </div>
          </article>
        ))}
      </main>
          )}
      
      {/* Render ExhibitSelector as a modal overlay at the top level */}
      {selectedArtwork && (
        <ExhibitSelector 
          artwork={selectedArtwork}
          onClose={() => setSelectedArtwork(null)}
        />
      )}

      {/* Welcome Modal */}
      <WelcomeModal 
        isOpen={showWelcomeModal}
        onClose={handleCloseWelcomeModal}
      />
        </>
      )}
    </div>
  );
}

function App() {
  const [textSize, setTextSize] = useState(() => {
    const stored = localStorage.getItem('textSize');
    return stored || 'default';
  });

  useEffect(() => {
    // Remove any existing text size classes
    document.body.classList.remove('text-extra-small', 'text-small', 'text-large', 'text-extra-large');
    
    // Add the current text size class
    if (textSize !== 'default') {
      document.body.classList.add(`text-${textSize}`);
    }
    
    localStorage.setItem('textSize', textSize);
  }, [textSize]);

  const handleTextSizeChange = useCallback((action) => {
    setTextSize((current) => {
      switch (action) {
        case 'increase':
          if (current === 'extra-small') return 'small';
          if (current === 'small') return 'default';
          if (current === 'default') return 'large';
          if (current === 'large') return 'extra-large';
          return current;
        case 'decrease':
          if (current === 'extra-large') return 'large';
          if (current === 'large') return 'default';
          if (current === 'default') return 'small';
          if (current === 'small') return 'extra-small';
          return current;
        case 'reset':
          return 'default';
        default:
          return current;
      }
    });
  }, []);

  return (
    <ExhibitProvider>
      <Router>
        <Routes>
          <Route path="/" element={<Home textSize={textSize} onTextSizeChange={handleTextSizeChange} />} />
          <Route path="/my-exhibits" element={<MyExhibits textSize={textSize} onTextSizeChange={handleTextSizeChange} />} />
        </Routes>
      </Router>
    </ExhibitProvider>
  );
}

export default App;
